// ============================================================
// Summit League v2 - AuthPage.tsx
// Matches existing SL design with new modular logic
// ============================================================

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import axiosClient from '../api/axiosClient';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import ToastContainer from '../components/ui/Toast';
import PasswordStrength from '../components/ui/PasswordStrength';
import CodeInput from '../components/ui/CodeInput';
import { useToast } from '../hooks/useToast';
import summitLogo from '../assets/summit-league-logo.png';
import '../styles/auth.css';

// ---------- SANITIZERS ----------
function stripControlChars(raw: string): string {
  return String(raw ?? '').split('')
    .filter((c) => {
      const code = c.charCodeAt(0);
      return code > 31 && code !== 127;
    })
    .join('');
}

function sanitizeText(
  raw: string,
  { trim = true, collapseSpaces = false, maxLen = 0 } = {}
): string {
  let v = stripControlChars(raw);
  if (collapseSpaces) v = v.replace(/\s+/g, ' ');
  if (trim) v = v.trim();
  if (maxLen > 0 && v.length > maxLen) v = v.slice(0, maxLen);
  return v;
}

function blurActiveInput(): void {
  const el = document.activeElement as HTMLElement;
  if (!el) return;
  const tag = (el.tagName || '').toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') {
    if (typeof el.blur === 'function') el.blur();
  }
}

// ---------- CONSTANTS ----------
const BARE_DOMAIN = (() => {
  const host = window.location.hostname.toLowerCase();
  // Local dev: strip any subdomain from *.localhost
  if (host === 'localhost' || host.endsWith('.localhost')) {
    return `${window.location.protocol}//localhost:${window.location.port}`;
  }
  // Production: always point to bare domain
  return `${window.location.protocol}//summitleague.gg`;
})();

// ---------- TYPES ----------
type AuthTab = 'login' | 'register';
type ModalView = 'verify' | 'forgot' | 'reset' | 'resend' | 'recover' | null;

export default function AuthPage() {
  const [activeTab, setActiveTab] = useState<AuthTab>('login');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { login: authLogin } = useAuthContext();
  const navigate = useNavigate();
  const { toasts, removeToast, success, error: toastError, info } = useToast();

  // ----- Login form -----
  const [loginForm, setLoginForm] = useState({
    username: '',
    password: '',
  });

  // ----- Register form -----
  const [registerForm, setRegisterForm] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  // ----- Registration consent checkboxes -----
  const [tosAccepted, setTosAccepted] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  // ----- Form messages -----
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  // ----- Modal state -----
  // Persist pending verification to sessionStorage so a Firefox password-manager
  // page reload doesn't lose the "verify email" modal.
  const [modalView, setModalView] = useState<ModalView>(() => {
    const saved = sessionStorage.getItem('sl_pending_verify');
    if (saved) return 'verify';
    return null;
  });
  const [pendingEmail, setPendingEmail] = useState(() => {
    const saved = sessionStorage.getItem('sl_pending_verify');
    if (saved) {
      try { return JSON.parse(saved).email ?? ''; } catch { return ''; }
    }
    return '';
  });
  const [pendingUsername, setPendingUsername] = useState(() => {
    const saved = sessionStorage.getItem('sl_pending_verify');
    if (saved) {
      try { return JSON.parse(saved).username ?? ''; } catch { return ''; }
    }
    return '';
  });

  // ----- Forgot password state -----
  const [forgotForm, setForgotForm] = useState({
    username: '',
    code: '',
    newPassword: '',
    confirmNewPassword: '',
  });
  const [forgotError, setForgotError] = useState('');
  const [isForgotSubmitting, setIsForgotSubmitting] = useState(false);

  // ----- Verify email state -----
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyError, setVerifyError] = useState('');
  const [isVerifySubmitting, setIsVerifySubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // ----- Resend verification state -----
  const [resendForm, setResendForm] = useState({ username: '', email: '' });
  const [resendError, setResendError] = useState('');
  const [isResendSubmitting, setIsResendSubmitting] = useState(false);

  // ----- Recover account state -----
  const [recoverForm, setRecoverForm] = useState({ email: '', password: '' });
  const [recoverError, setRecoverError] = useState('');
  const [isRecoverSubmitting, setIsRecoverSubmitting] = useState(false);

  // ----- Height animation -----
  const loginRef = useRef<HTMLDivElement>(null);
  const registerRef = useRef<HTMLDivElement>(null);
  const [formHeight, setFormHeight] = useState<number | 'auto'>('auto');

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Measure form height for animation
  useEffect(() => {
    const activeRef = activeTab === 'login' ? loginRef : registerRef;
    if (activeRef.current) {
      setFormHeight(activeRef.current.offsetHeight);
    }
  }, [activeTab, loginForm, registerForm, formError, formSuccess, tosAccepted, ageConfirmed]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setInterval(() => setResendCooldown((c) => c - 1), 1000);
      return () => clearInterval(timer);
    }
  }, [resendCooldown]);

  // ---------- LOGIN HANDLERS ----------
  const handleLoginChange = (field: keyof typeof loginForm, value: string) => {
    if (field === 'username') {
      const cleaned = sanitizeText(value, { trim: true, collapseSpaces: true, maxLen: 24 });
      setLoginForm((prev) => ({ ...prev, [field]: cleaned }));
    } else if (field === 'password') {
      const cleaned = sanitizeText(value, { trim: false, collapseSpaces: false, maxLen: 256 });
      setLoginForm((prev) => ({ ...prev, [field]: cleaned }));
    }
  };

  // ---------- REGISTER HANDLERS ----------
  const handleRegisterChange = (field: keyof typeof registerForm, value: string) => {
    if (field === 'username') {
      const cleaned = sanitizeText(value, { trim: true, collapseSpaces: true, maxLen: 24 });
      setRegisterForm((prev) => ({ ...prev, [field]: cleaned }));
    } else if (field === 'email') {
      const cleaned = sanitizeText(value, { trim: true, collapseSpaces: true, maxLen: 254 });
      setRegisterForm((prev) => ({ ...prev, [field]: cleaned }));
    } else {
      const cleaned = sanitizeText(value, { trim: false, collapseSpaces: false, maxLen: 256 });
      setRegisterForm((prev) => ({ ...prev, [field]: cleaned }));
    }
  };

  // ---------- MAIN FORM SUBMIT ----------
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    if (activeTab === 'login') {
      if (!loginForm.username || !loginForm.password) {
        setFormError('Please enter your username and password.');
        return;
      }

      try {
        setIsSubmitting(true);
        // Backend expects { username, password } where username accepts email too
        const res = await axiosClient.post('/login', {
          username: loginForm.username,
          password: loginForm.password,
        });

        if (res.data?.success) {
          // Token is handled via httpOnly cookie — response only contains user data
          const { user } = res.data.data || {};
          if (!user) {
            setFormError('Login succeeded, but no user data was returned.');
          } else {
            // Map snake_case SafeUser to camelCase frontend User
            const mappedUser = {
              userId: user.user_id,
              username: user.username,
              email: user.email,
              role: user.role,
              avatarUrl: user.avatar_url || undefined,
              emailVerified: user.email_verified,
              tosAcceptedVersion: user.tos_accepted_version ?? null,
              currentTosVersion: user.current_tos_version,
              ageConfirmed: user.age_confirmed ?? false,
            };
            authLogin(mappedUser);
            success('Welcome back!', 'Login successful');
            blurActiveInput();
            navigate('/', { replace: true });
          }
        } else {
          setFormError(res.data?.message || 'Login failed.');
        }
      } catch (err: any) {
        const apiMsg = err?.response?.data?.message || 'Login failed. Please try again.';

        // Check if account is pending deletion — open recovery modal
        if (apiMsg.toLowerCase().includes('scheduled for deletion')) {
          setModalView('recover');
          setFormError('');
          return;
        }

        // Check if email not verified
        if (err?.response?.status === 403 || apiMsg.toLowerCase().includes('verify')) {
          setPendingUsername(loginForm.username);
          setModalView('verify');
          toastError('Email Not Verified', 'Please verify your email to continue.');
        } else {
          setFormError(apiMsg);
        }
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // REGISTER
      if (!registerForm.username || !registerForm.email || !registerForm.password) {
        setFormError('Please fill in all required fields.');
        return;
      }
      if (registerForm.password !== registerForm.confirmPassword) {
        setFormError('Passwords do not match.');
        return;
      }
      if (!tosAccepted) {
        setFormError('You must agree to the Terms of Service and Privacy Policy.');
        return;
      }
      if (!ageConfirmed) {
        setFormError('You must confirm that you are at least 13 years of age.');
        return;
      }

      try {
        setIsSubmitting(true);
        const res = await axiosClient.post('/register', {
          username: registerForm.username,
          email: registerForm.email,
          password: registerForm.password,
          tosAccepted: true,
          ageConfirmed: true,
          marketingOptIn,
        });

        if (res.data?.success) {
          // Persist to sessionStorage BEFORE setting React state — Firefox's
          // password manager can reload the page after a successful registration,
          // which would lose React state before the verify modal renders.
          sessionStorage.setItem(
            'sl_pending_verify',
            JSON.stringify({ email: registerForm.email, username: registerForm.username })
          );
          success('Account Created!', 'Please check your email for a verification code.');
          setPendingEmail(registerForm.email);
          setPendingUsername(registerForm.username);
          setModalView('verify');
        } else {
          setFormError(res.data?.message || 'Registration failed.');
        }
      } catch (err: any) {
        const apiMsg = err?.response?.data?.message || 'Registration failed. Please try again.';
        setFormError(apiMsg);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // ---------- VERIFY EMAIL ----------
  // Backend: POST /confirm expects { email, code }
  const handleVerifySubmit = async () => {
    if (verifyCode.length < 6) {
      setVerifyError('Please enter the 6-digit code.');
      return;
    }

    setVerifyError('');
    setIsVerifySubmitting(true);

    try {
      const res = await axiosClient.post('/confirm', {
        email: pendingEmail,
        code: verifyCode,
      });

      if (res.data?.success) {
        sessionStorage.removeItem('sl_pending_verify');
        success('Email Verified!', 'You can now sign in to your account.');
        setModalView(null);
        setVerifyCode('');
        setActiveTab('login');
      } else {
        setVerifyError(res.data?.message || 'Verification failed.');
      }
    } catch (err: any) {
      setVerifyError(err?.response?.data?.message || 'Verification failed. Please try again.');
    } finally {
      setIsVerifySubmitting(false);
    }
  };

  // Backend: POST /resend-confirmation expects { email }
  const handleResendCode = async () => {
    if (resendCooldown > 0) return;

    try {
      await axiosClient.post('/resend-confirmation', {
        email: pendingEmail,
      });
      setResendCooldown(60);
      info('Code Sent', 'A new verification code has been sent to your email.');
    } catch (err: any) {
      toastError('Failed to Resend', err?.response?.data?.message || 'Please try again later.');
    }
  };

  // ---------- FORGOT PASSWORD ----------
  const handleForgotChange = (field: keyof typeof forgotForm, value: string) => {
    if (field === 'username') {
      const cleaned = sanitizeText(value, { trim: true, collapseSpaces: true, maxLen: 24 });
      setForgotForm((prev) => ({ ...prev, [field]: cleaned }));
    } else if (field === 'code') {
      setForgotForm((prev) => ({ ...prev, [field]: value }));
    } else {
      const cleaned = sanitizeText(value, { trim: false, collapseSpaces: false, maxLen: 256 });
      setForgotForm((prev) => ({ ...prev, [field]: cleaned }));
    }
  };

  // Backend: POST /forgot-password expects { username } (accepts username or email)
  const handleForgotRequest = async () => {
    if (!forgotForm.username) {
      setForgotError('Please enter your username.');
      return;
    }

    setForgotError('');
    setIsForgotSubmitting(true);

    try {
      const res = await axiosClient.post('/forgot-password', {
        username: forgotForm.username,
      });

      if (res.data?.success) {
        info('Reset Code Sent', 'Check your email for a password reset code.');
        setPendingUsername(forgotForm.username);
        setModalView('reset');
      } else {
        setForgotError(res.data?.message || 'Could not send reset code.');
      }
    } catch (err: any) {
      setForgotError(err?.response?.data?.message || 'Could not send reset code.');
    } finally {
      setIsForgotSubmitting(false);
    }
  };

  // ---------- RESET PASSWORD ----------
  // Backend: POST /reset-password expects { username, code, newPassword }
  const handleResetSubmit = async () => {
    if (!forgotForm.code) {
      setForgotError('Please enter the verification code.');
      return;
    }
    if (!forgotForm.newPassword) {
      setForgotError('Please enter a new password.');
      return;
    }
    if (forgotForm.newPassword !== forgotForm.confirmNewPassword) {
      setForgotError('Passwords do not match.');
      return;
    }

    setForgotError('');
    setIsForgotSubmitting(true);

    try {
      const res = await axiosClient.post('/reset-password', {
        username: pendingUsername,
        code: forgotForm.code,
        newPassword: forgotForm.newPassword,
      });

      if (res.data?.success) {
        success('Password Reset!', 'You can now sign in with your new password.');
        setModalView(null);
        setForgotForm({ username: '', code: '', newPassword: '', confirmNewPassword: '' });
      } else {
        setForgotError(res.data?.message || 'Could not reset password.');
      }
    } catch (err: any) {
      setForgotError(err?.response?.data?.message || 'Could not reset password.');
    } finally {
      setIsForgotSubmitting(false);
    }
  };

  // ---------- RESEND VERIFICATION (from register tab) ----------
  const handleResendFormChange = (field: keyof typeof resendForm, value: string) => {
    const cleaned = sanitizeText(value, { trim: true, collapseSpaces: true, maxLen: field === 'email' ? 254 : 24 });
    setResendForm((prev) => ({ ...prev, [field]: cleaned }));
  };

  // Backend: POST /resend-confirmation expects { email }
  const handleResendFormSubmit = async () => {
    if (!resendForm.username) {
      setResendError('Please enter your username.');
      return;
    }
    if (!resendForm.email) {
      setResendError('Please enter the email on your account.');
      return;
    }

    setResendError('');
    setIsResendSubmitting(true);

    try {
      const res = await axiosClient.post('/resend-confirmation', {
        email: resendForm.email,
      });

      if (res.data?.success) {
        info('Code Sent', 'A new verification code has been sent to your email.');
        // Transition to verify modal so user can enter the code
        sessionStorage.setItem(
          'sl_pending_verify',
          JSON.stringify({ email: resendForm.email, username: resendForm.username })
        );
        setPendingEmail(resendForm.email);
        setPendingUsername(resendForm.username);
        setResendForm({ username: '', email: '' });
        setResendError('');
        setVerifyCode('');
        setVerifyError('');
        setResendCooldown(60);
        setModalView('verify');
      } else {
        setResendError(res.data?.message || 'Could not resend verification email.');
      }
    } catch (err: any) {
      setResendError(err?.response?.data?.message || 'Could not resend verification email.');
    } finally {
      setIsResendSubmitting(false);
    }
  };

  // ---------- RECOVER ACCOUNT ----------
  const handleRecoverChange = (field: keyof typeof recoverForm, value: string) => {
    if (field === 'email') {
      const cleaned = sanitizeText(value, { trim: true, collapseSpaces: true, maxLen: 254 });
      setRecoverForm((prev) => ({ ...prev, [field]: cleaned }));
    } else {
      const cleaned = sanitizeText(value, { trim: false, collapseSpaces: false, maxLen: 256 });
      setRecoverForm((prev) => ({ ...prev, [field]: cleaned }));
    }
  };

  // Backend: POST /recover-account expects { email, password }
  const handleRecoverSubmit = async () => {
    if (!recoverForm.email || !recoverForm.password) {
      setRecoverError('Please enter your email and password.');
      return;
    }

    setRecoverError('');
    setIsRecoverSubmitting(true);

    try {
      const res = await axiosClient.post('/recover-account', {
        email: recoverForm.email,
        password: recoverForm.password,
      });

      if (res.data?.success) {
        success('Account Recovered!', 'Welcome back! Please sign in.');
        setModalView(null);
        setRecoverForm({ email: '', password: '' });
        setRecoverError('');
        setActiveTab('login');
      } else {
        setRecoverError(res.data?.message || 'Recovery failed.');
      }
    } catch (err: any) {
      setRecoverError(err?.response?.data?.message || 'Recovery failed. Please try again.');
    } finally {
      setIsRecoverSubmitting(false);
    }
  };

  // ---------- CLOSE MODAL ----------
  const closeModal = () => {
    sessionStorage.removeItem('sl_pending_verify');
    setModalView(null);
    setVerifyCode('');
    setVerifyError('');
    setForgotForm({ username: '', code: '', newPassword: '', confirmNewPassword: '' });
    setForgotError('');
    setResendForm({ username: '', email: '' });
    setResendError('');
    setRecoverForm({ email: '', password: '' });
    setRecoverError('');
  };

  // ---------- RENDER LOGIN FORM ----------
  const renderLoginForm = () => (
    <form className="sl-auth-form" onSubmit={handleSubmit}>
      <Input
        id="login-username"
        label="Username"
        type="text"
        value={loginForm.username}
        onChange={(e) => handleLoginChange('username', e.target.value)}
        autoComplete="username"
      />

      <Input
        id="login-password"
        label="Password"
        type="password"
        value={loginForm.password}
        onChange={(e) => handleLoginChange('password', e.target.value)}
        autoComplete="current-password"
        showPasswordToggle
      />

      {formError && <div className="sl-auth-error">{formError}</div>}
      {formSuccess && <div className="sl-auth-success">{formSuccess}</div>}

      <Button type="submit" variant="primary" size="md" fullWidth disabled={isSubmitting}>
        {isSubmitting ? 'Signing in...' : 'Sign In'}
      </Button>

      <div className="sl-auth-footer">
        <button type="button" className="sl-auth-link" onClick={() => setModalView('forgot')}>
          Forgot your password?
        </button>
      </div>
    </form>
  );

  // ---------- RENDER REGISTER FORM ----------
  const renderRegisterForm = () => (
    <form className="sl-auth-form" onSubmit={handleSubmit}>
      <Input
        id="register-username"
        label="Username"
        type="text"
        value={registerForm.username}
        onChange={(e) => handleRegisterChange('username', e.target.value)}
        autoComplete="username"
        helperText="Your Summit League handle. This will be visible on leaderboards."
      />

      <Input
        id="register-email"
        label="Email"
        type="email"
        value={registerForm.email}
        onChange={(e) => handleRegisterChange('email', e.target.value)}
        autoComplete="email"
      />

      <div>
        <Input
          id="register-password"
          label="Password"
          type="password"
          value={registerForm.password}
          onChange={(e) => handleRegisterChange('password', e.target.value)}
          autoComplete="new-password"
          showPasswordToggle
        />
        <PasswordStrength password={registerForm.password} />
      </div>

      <Input
        id="register-confirm-password"
        label="Confirm Password"
        type="password"
        value={registerForm.confirmPassword}
        onChange={(e) => handleRegisterChange('confirmPassword', e.target.value)}
        autoComplete="new-password"
        showPasswordToggle
      />

      {/* ── Consent Checkboxes ──────────────────────── */}
      <div className="sl-auth-consent">
        <label className="sl-auth-checkbox">
          <input
            type="checkbox"
            checked={tosAccepted}
            onChange={(e) => setTosAccepted(e.target.checked)}
          />
          <span className="sl-auth-checkbox-mark" />
          <span className="sl-auth-checkbox-label">
            I agree to the{' '}
            <a
              href={`${BARE_DOMAIN}/legal?tab=terms`}
              target="_blank"
              rel="noreferrer"
              className="sl-auth-checkbox-link"
            >
              Terms of Service
            </a>
            {' '}and{' '}
            <a
              href={`${BARE_DOMAIN}/legal?tab=privacy`}
              target="_blank"
              rel="noreferrer"
              className="sl-auth-checkbox-link"
            >
              Privacy Policy
            </a>
          </span>
        </label>

        <label className="sl-auth-checkbox">
          <input
            type="checkbox"
            checked={ageConfirmed}
            onChange={(e) => setAgeConfirmed(e.target.checked)}
          />
          <span className="sl-auth-checkbox-mark" />
          <span className="sl-auth-checkbox-label">
            I confirm that I am at least 13 years of age
          </span>
        </label>

        <label className="sl-auth-checkbox sl-auth-checkbox--optional">
          <input
            type="checkbox"
            checked={marketingOptIn}
            onChange={(e) => setMarketingOptIn(e.target.checked)}
          />
          <span className="sl-auth-checkbox-mark" />
          <span className="sl-auth-checkbox-label">
            Send me updates about Summit League news, seasons, and events
            <span className="sl-auth-checkbox-optional-tag">Optional</span>
          </span>
        </label>
      </div>

      {formError && <div className="sl-auth-error">{formError}</div>}
      {formSuccess && <div className="sl-auth-success">{formSuccess}</div>}

      <Button type="submit" variant="primary" size="md" fullWidth disabled={isSubmitting}>
        {isSubmitting ? 'Creating account...' : 'Create Account'}
      </Button>

      <div className="sl-auth-footer">
        <button type="button" className="sl-auth-link" onClick={() => setModalView('resend')}>
          Need to resend your verification email?
        </button>
      </div>
    </form>
  );

  // Mask email for display
  const maskedEmail = pendingEmail
  ? pendingEmail.replace(/^(.{2})(.*)(@.*)$/, (_: string, start: string, middle: string, end: string) =>
      start + '*'.repeat(Math.min(middle.length, 6)) + end
    )
  : '';

  return (
    <div className="sl-auth-root">
      <div className="sl-auth-card">
        {/* Header */}
        <div className="sl-auth-header">
          <img src={summitLogo} alt="Summit League Logo" className="sl-auth-logo" />

          <div className="sl-auth-subtitle">
            Login or create your account to climb the season ladder.
          </div>
        </div>

        {/* Tabs */}
        <div className="sl-auth-tabs">
          <button
            type="button"
            className={`sl-auth-tab ${activeTab === 'login' ? 'sl-auth-tab--active' : ''}`}
            onClick={() => {
              setFormError('');
              setFormSuccess('');
              setActiveTab('login');
            }}
          >
            Login
          </button>
          <button
            type="button"
            className={`sl-auth-tab ${activeTab === 'register' ? 'sl-auth-tab--active' : ''}`}
            onClick={() => {
              setFormError('');
              setFormSuccess('');
              setActiveTab('register');
            }}
          >
            Register
          </button>
        </div>

        {/* Forms */}
        <div className="sl-auth-form-wrapper" style={{ height: formHeight }}>
          <div
            ref={loginRef}
            style={{
              position: activeTab === 'login' ? 'relative' : 'absolute',
              inset: 0,
              opacity: activeTab === 'login' ? 1 : 0,
              transform: `translateY(${activeTab === 'login' ? '0' : '8px'})`,
              pointerEvents: activeTab === 'login' ? 'auto' : 'none',
              transition: 'opacity 0.2s ease, transform 0.2s ease',
            }}
          >
            {renderLoginForm()}
          </div>

          <div
            ref={registerRef}
            style={{
              position: activeTab === 'register' ? 'relative' : 'absolute',
              inset: 0,
              opacity: activeTab === 'register' ? 1 : 0,
              transform: `translateY(${activeTab === 'register' ? '0' : '8px'})`,
              pointerEvents: activeTab === 'register' ? 'auto' : 'none',
              transition: 'opacity 0.2s ease, transform 0.2s ease',
            }}
          >
            {renderRegisterForm()}
          </div>
        </div>
      </div>

      {/* ========== MODALS ========== */}

      {/* Verify Email Modal */}
      <Modal
        isOpen={modalView === 'verify'}
        title="Verify Your Email"
        onClose={closeModal}
        onCancel={closeModal}
        cancelLabel="Back to Login"
        confirmLabel={isVerifySubmitting ? 'Verifying...' : 'Verify Email'}
        onConfirm={handleVerifySubmit}
        disableConfirm={verifyCode.length < 6 || isVerifySubmitting}
      >
        <div style={{ textAlign: 'center' }}>
          <div className="sl-verify-icon">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <rect x="4" y="10" width="40" height="28" rx="4" stroke="#38bdf8" strokeWidth="2" />
              <polyline points="4,14 24,28 44,14" stroke="#38bdf8" strokeWidth="2" fill="none" />
              <circle cx="38" cy="14" r="8" fill="#4ade80" stroke="#1a2744" strokeWidth="2" />
              <path d="M35 14l2 2 4-4" stroke="#1a2744" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          <p style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '20px' }}>
            We sent a 6-digit code to {maskedEmail ? <strong>{maskedEmail}</strong> : 'your email'}
          </p>

          <CodeInput
            value={verifyCode}
            onChange={(val) => {
              setVerifyCode(val);
              setVerifyError('');
            }}
            error={verifyError}
            disabled={isVerifySubmitting}
          />

          <div className="sl-verify-actions" style={{ marginTop: '16px' }}>
            <button
              type="button"
              className={`sl-auth-link ${resendCooldown > 0 ? 'disabled' : ''}`}
              onClick={handleResendCode}
              disabled={resendCooldown > 0}
              style={{ opacity: resendCooldown > 0 ? 0.5 : 1 }}
            >
              {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Forgot Password Modal */}
      <Modal
        isOpen={modalView === 'forgot'}
        title="Reset Password"
        onClose={closeModal}
        onCancel={closeModal}
        cancelLabel="Cancel"
        confirmLabel={isForgotSubmitting ? 'Sending...' : 'Send Reset Code'}
        onConfirm={handleForgotRequest}
        disableConfirm={!forgotForm.username || isForgotSubmitting}
      >
        <p style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '16px' }}>
          Enter your Summit League username and we'll send a reset code to your email.
        </p>

        <Input
          id="forgot-username"
          label="Username"
          type="text"
          value={forgotForm.username}
          onChange={(e) => handleForgotChange('username', e.target.value)}
          autoComplete="username"
        />

        {forgotError && <div className="sl-auth-error" style={{ marginTop: '8px' }}>{forgotError}</div>}
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        isOpen={modalView === 'reset'}
        title="New Password"
        onClose={closeModal}
        onCancel={closeModal}
        cancelLabel="Cancel"
        confirmLabel={isForgotSubmitting ? 'Resetting...' : 'Reset Password'}
        onConfirm={handleResetSubmit}
        disableConfirm={!forgotForm.code || !forgotForm.newPassword || isForgotSubmitting}
      >
        <p style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '16px' }}>
          Enter the code from your email and choose a new password.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.82rem', color: '#94a3b8', marginBottom: '6px' }}>
              Verification Code
            </label>
            <CodeInput
              value={forgotForm.code}
              onChange={(val) => handleForgotChange('code', val)}
              disabled={isForgotSubmitting}
            />
          </div>

          <Input
            id="reset-new-password"
            label="New Password"
            type="password"
            value={forgotForm.newPassword}
            onChange={(e) => handleForgotChange('newPassword', e.target.value)}
            autoComplete="new-password"
            showPasswordToggle
          />

          <Input
            id="reset-confirm-password"
            label="Confirm New Password"
            type="password"
            value={forgotForm.confirmNewPassword}
            onChange={(e) => handleForgotChange('confirmNewPassword', e.target.value)}
            autoComplete="new-password"
            showPasswordToggle
          />
        </div>

        {forgotError && <div className="sl-auth-error" style={{ marginTop: '8px' }}>{forgotError}</div>}
      </Modal>

      {/* Resend Verification Modal */}
      <Modal
        isOpen={modalView === 'resend'}
        title="Resend Verification Email"
        onClose={closeModal}
        onCancel={closeModal}
        cancelLabel="Cancel"
        confirmLabel={isResendSubmitting ? 'Sending...' : 'Resend Email'}
        onConfirm={handleResendFormSubmit}
        disableConfirm={!resendForm.username || !resendForm.email || isResendSubmitting}
      >
        <p style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '16px' }}>
          Enter the username and email you used to register. We'll send a new verification link.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Input
            id="resend-username"
            label="Username"
            type="text"
            value={resendForm.username}
            onChange={(e) => handleResendFormChange('username', e.target.value)}
            autoComplete="username"
          />

          <Input
            id="resend-email"
            label="Email"
            type="email"
            value={resendForm.email}
            onChange={(e) => handleResendFormChange('email', e.target.value)}
            autoComplete="email"
          />
        </div>

        {resendError && <div className="sl-auth-error" style={{ marginTop: '8px' }}>{resendError}</div>}
      </Modal>

      {/* Recover Deleted Account Modal */}
      <Modal
        isOpen={modalView === 'recover'}
        title="Recover Your Account"
        onClose={closeModal}
        onCancel={closeModal}
        cancelLabel="Cancel"
        confirmLabel={isRecoverSubmitting ? 'Recovering...' : 'Recover Account'}
        onConfirm={handleRecoverSubmit}
        disableConfirm={!recoverForm.email || !recoverForm.password || isRecoverSubmitting}
      >
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <circle cx="24" cy="24" r="20" stroke="#f59e0b" strokeWidth="2" fill="none" />
            <path d="M24 14v12" stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round" />
            <circle cx="24" cy="32" r="1.5" fill="#f59e0b" />
          </svg>
        </div>

        <p style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '16px', textAlign: 'center' }}>
          Your account is scheduled for deletion. Enter your email and password to recover it.
          After the 7-day window, recovery is no longer possible.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Input
            id="recover-email"
            label="Email"
            type="email"
            value={recoverForm.email}
            onChange={(e) => handleRecoverChange('email', e.target.value)}
            autoComplete="email"
          />

          <Input
            id="recover-password"
            label="Password"
            type="password"
            value={recoverForm.password}
            onChange={(e) => handleRecoverChange('password', e.target.value)}
            autoComplete="current-password"
            showPasswordToggle
          />
        </div>

        {recoverError && <div className="sl-auth-error" style={{ marginTop: '8px' }}>{recoverError}</div>}
      </Modal>

      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}